import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Search, Users, Mail, Phone, ShoppingBag } from "lucide-react";
import { BlacklistTab } from "@/components/BlacklistTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (n) => `${(Number(n) || 0).toFixed(2)} RON`;

// User request Feb 2026: filter Clienti prin 9 taburi de sursa.
const SOURCE_TABS = [
  { key: "emag_ro_bau",  label: "eMAG RO BAU" },
  { key: "emag_ro_fbe",  label: "eMAG RO FBE" },
  { key: "emag_bg_bau",  label: "eMAG BG BAU" },
  { key: "emag_bg_fbe",  label: "eMAG BG FBE" },
  { key: "emag_hu_bau",  label: "eMAG HU BAU" },
  { key: "emag_hu_fbe",  label: "eMAG HU FBE" },
  { key: "gomag_bragus", label: "Gomag Bragus" },
  { key: "manual_bragus", label: "Manual Bragus" },
  { key: "trendyol",     label: "Trendyol" },
];

export default function Customers() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [search, setSearch] = useState("");
  const [source, setSource] = useState("emag_ro_bau");  // Default first tab
  const [loading, setLoading] = useState(true);
  const [showBl, setShowBl] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/customers`, {
        params: { page, per_page: perPage, search: search || undefined, source },
      });
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search, source]);

  const fetchStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/customers/stats`, { params: { source } });
      setStats(r.data);
    } catch (_e) { /* ignore */ }
  }, [source]);

  useEffect(() => { fetchData(); fetchStats(); }, [fetchData, fetchStats]);

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8" data-testid="customers-page">
      <div className="mb-5">
        <div className="text-[11px] text-zinc-500">Panou de control / <span className="text-zinc-300">Clienți</span></div>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-50">Clienți</h1>
        <p className="text-sm text-zinc-500">Toți clienții care au plasat cel puțin o comandă.</p>
      </div>

      {stats && (
        <div className="mb-5 grid grid-cols-2 sm:grid-cols-2 gap-3 max-w-2xl">
          <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Clienți unici</div>
            <div className="mt-1 text-xl font-semibold text-zinc-100">{stats.unique_customers}</div>
          </div>
          <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Cu email</div>
            <div className="mt-1 text-xl font-semibold text-sky-300">{stats.with_email}</div>
          </div>
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-1.5 border-b border-zinc-800 pb-3" data-testid="customer-source-tabs">
        {SOURCE_TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`cust-tab-${t.key}`}
            onClick={() => { setSource(t.key); setPage(1); setShowBl(false); }}
            className={`rounded-full border px-3 py-1.5 text-[11px] font-medium transition ${
              source === t.key && !showBl
                ? "border-orange-500/50 bg-orange-500/15 text-orange-200"
                : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200"
            }`}
          >
            {t.label}
          </button>
        ))}
        <button
          data-testid="cust-tab-blacklist"
          onClick={() => setShowBl(true)}
          className={`rounded-full border px-3 py-1.5 text-[11px] font-medium transition ${showBl ? "border-rose-500/50 bg-rose-500/15 text-rose-200" : "border-zinc-800 bg-zinc-950 text-rose-300/80 hover:border-rose-500/40"}`}
        >
          Blacklist
        </button>
      </div>

      {showBl ? <BlacklistTab /> : (<>
      <div className="mb-4 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
        <input data-testid="cust-search" placeholder="Caută nume, telefon sau email..." value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="w-full rounded-lg border border-zinc-800 bg-zinc-950 pl-9 pr-3 py-2 text-sm text-zinc-100" />
      </div>

      <div className="overflow-x-auto rounded-xl border border-zinc-800">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60 text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="px-3 py-2 text-left">Nume prenume</th>
              <th className="px-3 py-2 text-left">Telefon</th>
              <th className="px-3 py-2 text-left">Email</th>
              <th className="px-3 py-2 text-right">Comenzi</th>
              <th className="px-3 py-2 text-right">Total cheltuit</th>
              <th className="px-3 py-2 text-left">Ultima comandă</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-3 py-8 text-center text-xs text-zinc-500">Se încarcă...</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-12 text-center text-xs text-zinc-500">
                <Users className="mx-auto mb-2 h-6 w-6 text-zinc-700" />
                Niciun client găsit.
              </td></tr>
            )}
            {!loading && items.map((c, idx) => (
              <tr key={`${c.phone || c.email || c.name}-${idx}`} className="border-t border-zinc-900 hover:bg-zinc-900/40">
                <td className="px-3 py-2 font-medium text-zinc-100">{c.name || "—"}</td>
                <td className="px-3 py-2 text-zinc-300">
                  {c.phone ? (
                    <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 hover:text-sky-300">
                      <Phone className="h-3 w-3" /> {c.phone}
                    </a>
                  ) : <span className="text-zinc-600">—</span>}
                </td>
                <td className="px-3 py-2 text-zinc-300">
                  {c.email ? (
                    <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 hover:text-sky-300 truncate max-w-[220px]" title={c.email}>
                      <Mail className="h-3 w-3" /> <span className="truncate">{c.email}</span>
                    </a>
                  ) : <span className="text-zinc-600">—</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  <span className="inline-flex items-center gap-1 rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] font-medium text-zinc-200">
                    <ShoppingBag className="h-3 w-3" /> {c.order_count}
                  </span>
                </td>
                <td className="px-3 py-2 text-right text-emerald-300">{fmt(c.total_spent)}</td>
                <td className="px-3 py-2 text-zinc-400 text-xs">
                  <div>#{c.last_order_id}</div>
                  <div className="text-[10px] text-zinc-500">{c.last_order_date} · {c.last_integration_key}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total > perPage && (
        <div className="mt-3 flex items-center justify-between text-xs text-zinc-500">
          <div>Afișez {(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} din {total}</div>
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
              className="rounded border border-zinc-800 px-2 py-1 disabled:opacity-40">‹ Înapoi</button>
            <button onClick={() => setPage((p) => p + 1)} disabled={page * perPage >= total}
              className="rounded border border-zinc-800 px-2 py-1 disabled:opacity-40">Înainte ›</button>
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}
